import { AdyenCheckout, ICore } from '@adyen/adyen-web';
import { AdyenPaymentMethod, getCountryCode, LocaleCode } from '@appliedsystems/payments-core';
import { useEffect, useMemo, useState } from 'react';
import { ErrorMessage } from 'src/components/ErrorAlert/ErrorAlert';
import { AdyenCheckoutState } from 'src/util/adyen';
import { currencyMap } from '../constants/constants';
import { getConfig } from '../util/config';
import { usePaymentsTranslation } from './usePaymentsTranslation';

type Props = {
  locale: LocaleCode;
  total: number;
  handleOnSubmit: (state: AdyenCheckoutState, component: any) => void;
};

export type AdyenConfiguration = ICore['options'];

export const useAdyenConfig = ({ locale, total, handleOnSubmit }: Props) => {
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();
  const { t } = usePaymentsTranslation();

  const configuration = useMemo(
    () =>
      ({
        locale,
        translations: {
          en_US: {
            'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
            'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
            'creditCard.securityCode.label': t('SECURITY_CODE'),
            'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
            'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
            'cc.num.900': t('CARD_NUMBER_ERROR'),
            'cc.dat.910': t('EXPIRY_DATE_ERROR'),
            'cc.cvc.920': t('CVC_ERROR'),
            storeDetails: t('SAVE_FOR_NEXT_PAYMENT'),
          },
          en_CA: {
            'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
            'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
            'creditCard.securityCode.label': t('SECURITY_CODE'),
            'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
            'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
            'cc.num.900': t('CARD_NUMBER_ERROR'),
            'cc.dat.910': t('EXPIRY_DATE_ERROR'),
            'cc.cvc.920': t('CVC_ERROR'),
            storeDetails: t('SAVE_FOR_NEXT_PAYMENT'),
          },
          fr_CA: {
            'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
            'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
            'creditCard.securityCode.label': t('SECURITY_CODE'),
            'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
            'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
            'cc.num.900': t('CARD_NUMBER_ERROR'),
            'cc.dat.910': t('EXPIRY_DATE_ERROR'),
            'cc.cvc.920': t('CVC_ERROR'),
            storeDetails: t('SAVE_FOR_NEXT_PAYMENT'),
          },
        },
        environment: getConfig('REACT_APP_ADYEN_ENVIRONMENT'),
        clientKey: getConfig('REACT_APP_ADYEN_CLIENT_KEY'),
        analytics: {
          enabled: true,
        },
        paymentMethodsResponse: {
          paymentMethods: [
            {
              brands: ['visa', 'mc', 'amex', 'discover'],
              name: 'Credit Card',
              type: AdyenPaymentMethod.Card,
            },
            {
              name: 'ACH Direct Debit',
              type: AdyenPaymentMethod.Ach,
            },
          ],
        },
        showPayButton: false,
        onSubmit: handleOnSubmit,
        onError: (error) => {
          console.error('V2 Adyen onError called', error);
          return;
        },
        amount: {
          value: total,
          currency: currencyMap[locale],
        },
        onAdditionalDetails: () => {}, // what should this do?
        countryCode: getCountryCode(locale) ?? 'US', // This is complusory for Adyen since v6
      } satisfies AdyenConfiguration),
    [locale, t, handleOnSubmit, total],
  );

  const [checkout, setCheckout] = useState<ICore>();

  useEffect(() => {
    void (async function updateCheckout() {
      try {
        if (!checkout) setCheckout(await AdyenCheckout(configuration));
        else await checkout.update(configuration);
        setErrorMessage(undefined);
      } catch (err: unknown) {
        console.error('Error creating Adyen Checkout', err);
        setErrorMessage(['INIT_ADYEN_CHECKOUT_FAILED']);
      }
    })();
  }, [checkout, configuration]);

  return { configuration, checkout, errorMessage };
};
